import { TimeUnitEnum } from "@/enums/TimeUnitEnum";
import { Interval } from "@/interfaces/Interval";

export const useTime = () => {
  const convertToSeconds = (value: number, unit: TimeUnitEnum): number => {
    switch (unit) {
      case TimeUnitEnum.DAYS:
        return value * 86400;
      case TimeUnitEnum.HOURS:
        return value * 3600;
      case TimeUnitEnum.MINUTES:
        return value * 60;
      case TimeUnitEnum.SECONDS:
      default:
        return value;
    }
  };

  const convertIntervalToSeconds = (interval: Interval): number => {
    return (interval?.days || 0) * 86400 +
    (interval?.hours || 0) * 3600 +
    (interval?.minutes || 0) * 60 +
    (interval?.seconds || 0);
  }

  const convertFromSeconds = (seconds: number) => {
    if (seconds >= 86400 && seconds % 86400 === 0) {
      return { value: seconds / 86400, unit: TimeUnitEnum.DAYS };
    }
    if (seconds >= 3600 && seconds % 3600 === 0) {
      return { value: seconds / 3600, unit: TimeUnitEnum.HOURS };
    }
    if (seconds >= 60 && seconds % 60 === 0) {
      return { value: seconds / 60, unit: TimeUnitEnum.MINUTES };
    }
    return { value: seconds, unit: TimeUnitEnum.SECONDS };
  };

  const convertToInterval = (value: number, unit: TimeUnitEnum): Interval => {
    switch (unit) {
      case TimeUnitEnum.MINUTES:
        return value >= 60 ? { hours: Math.floor(value / 60), minutes: value % 60 } : { minutes: value };
      case TimeUnitEnum.SECONDS:
        return value >= 60 ? { minutes: Math.floor(value / 60), seconds: value % 60 } : { seconds: value };
      case TimeUnitEnum.HOURS:
        return { hours: value }; // Mantém em horas mesmo se ultrapassar 24
      case TimeUnitEnum.DAYS:
        return { days: value };
      default:
        return {};
    }
  };

  const formatInterval = (duration: Interval): string => {
    let { seconds = 0, minutes = 0, hours = 0 } = duration;
  
    // Converte os segundos para minutos
    minutes += Math.floor(seconds / 60);
    seconds %= 60;
  
    // Converte os minutos para horas
    hours += Math.floor(minutes / 60);
    minutes %= 60;
  
    // Converte as horas para dias
    const days = Math.floor(hours / 24);
    hours %= 24;
  
    // Retorna o formato apropriado
    if (days > 0) {
        return `${days}d`;
    } else if (hours > 0) {
        return `${hours}h`;
    } else if (minutes > 0) {
        return `${minutes}m`;
    } else {
        return `${seconds}s`;  // Exibe em segundos caso não haja minutos, horas ou dias
    }
  }

  function intervalToHHMM(interval: Interval): string {
    // Aproximações comuns: 1 mês = 30 dias, 1 ano = 365 dias
    const totalDays =
      (interval.years ?? 0) * 365 +
      (interval.months ?? 0) * 30 +
      (interval.days ?? 0);
  
    let totalMinutes =
      totalDays * 24 * 60 +
      (interval.hours ?? 0) * 60 +
      (interval.minutes ?? 0) +
      Math.floor((interval.seconds ?? 0) / 60);
  
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
  
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}`;
  }

  const convertIntervalToMinutes = (interval: Interval): number => {
    const totalDays = (interval.years ?? 0) * 365 + (interval.months ?? 0) * 30 + (interval.days ?? 0);
    return totalDays * 24 * 60 +
      (interval.hours ?? 0) * 60 +
      (interval.minutes ?? 0) +
      Math.floor((interval.seconds ?? 0) / 60);
  };

  const convertIntervalToDays = (interval: Interval): number => {
    const daysFromYears = (interval.years ?? 0) * 365;
    const daysFromMonths = (interval.months ?? 0) * 30;
    const days = (interval.days ?? 0);
  
    const daysFromHours = (interval.hours ?? 0) / 24;
    const daysFromMinutes = (interval.minutes ?? 0) / 1440;
    const daysFromSeconds = (interval.seconds ?? 0) / 86400;
  
    return daysFromYears + daysFromMonths + days + daysFromHours + daysFromMinutes + daysFromSeconds;
  }

  return { convertToSeconds, convertIntervalToSeconds, convertFromSeconds, convertToInterval, formatInterval, intervalToHHMM, convertIntervalToMinutes, convertIntervalToDays };
};